export interface TrackedAriOperation {
  id: string;
  trackedAt: string;
}

const MAX_AGE_MS = 24 * 60 * 60 * 1000;
const MAX_OPERATIONS = 50;
const volatileValues = new Map<string, string>();
const keyPrefix = 'stayon.ari.operations.';
const key = (propertyId: string) => `${keyPrefix}${propertyId}`;
const eventName = (propertyId: string) => `stayon:ari-operations:${propertyId}`;

function isCurrent(entry: TrackedAriOperation): boolean {
  return (
    typeof entry?.id === 'string' &&
    typeof entry?.trackedAt === 'string' &&
    Date.now() - Date.parse(entry.trackedAt) <= MAX_AGE_MS
  );
}

export function readTrackedAriOperations(propertyId: string): TrackedAriOperation[] {
  try {
    const storageKey = key(propertyId);
    const raw = volatileValues.get(storageKey) ?? sessionStorage.getItem(storageKey) ?? '[]';
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const current = parsed.filter(isCurrent).slice(-MAX_OPERATIONS);
    if (current.length !== parsed.length) write(propertyId, current);
    return current;
  } catch {
    return [];
  }
}

export function addTrackedAriOperations(propertyId: string, ids: string[]): void {
  const entries = readTrackedAriOperations(propertyId);
  const known = new Set(entries.map((entry) => entry.id));
  const trackedAt = new Date().toISOString();
  for (const id of ids) {
    if (id && !known.has(id)) {
      entries.push({ id, trackedAt });
      known.add(id);
    }
  }
  write(propertyId, entries.slice(-MAX_OPERATIONS));
  window.dispatchEvent(new Event(eventName(propertyId)));
}

export function removeTrackedAriOperation(propertyId: string, id: string): void {
  write(
    propertyId,
    readTrackedAriOperations(propertyId).filter((entry) => entry.id !== id),
  );
  window.dispatchEvent(new Event(eventName(propertyId)));
}

/** Ein Wechsel von Benutzer oder Workspace beginnt ohne die Übertragungen der vorigen Sitzung. */
export function clearTrackedAriOperations(): void {
  volatileValues.clear();
  try {
    for (const storageKey of Object.keys(sessionStorage)) {
      if (storageKey.startsWith(keyPrefix)) sessionStorage.removeItem(storageKey);
    }
  } catch {
    // Ohne sessionStorage gab es nur die flüchtigen Werte, und die sind oben schon geleert.
  }
}

export function subscribeToTrackedAriOperations(
  propertyId: string,
  listener: () => void,
): () => void {
  const event = eventName(propertyId);
  window.addEventListener(event, listener);
  window.addEventListener('storage', listener);
  return () => {
    window.removeEventListener(event, listener);
    window.removeEventListener('storage', listener);
  };
}

function write(propertyId: string, entries: TrackedAriOperation[]): void {
  const storageKey = key(propertyId);
  const serialized = JSON.stringify(entries);
  try {
    sessionStorage.setItem(storageKey, serialized);
    volatileValues.delete(storageKey);
  } catch {
    volatileValues.set(storageKey, serialized);
  }
}
